import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { GenericProps, type Orientation } from '@lumapps/lumx/react';

import { BASE_CLASSNAME as CLASSNAME } from '../constants';

export interface MenuProps extends GenericProps {
    /** HTML id */
    id?: string;
    /** Menu content */
    children: React.ReactNode;
    /** The width the menu must take. */
    width?: React.CSSProperties['width'];
    /** Menu orientation */
    orientation?: Orientation;
}

/**
 * Base menu list (ARIA `menu`)
 * Use Menu.Trigger & Menu.TriggerButton to attach it to a trigger button in a popup.
 *
 * @family Menus
 */
export const Menu = React.forwardRef((props: MenuProps, ref: React.Ref<HTMLElement>) => {
    const { id, className, children, role = 'menu', orientation = 'vertical', ...forwardedProps } = props;
    const { block } = useClassnames(CLASSNAME);

    return (
        <ul
            ref={ref as React.Ref<HTMLUListElement>}
            role={role}
            className={block(
                {
                    [`orientation-${orientation}`]: true,
                },
                [className, 'lumx-list', 'lumx-list--item-padding-big'],
            )}
            id={id}
            aria-orientation={orientation}
            {...forwardedProps}
        >
            {children}
        </ul>
    );
});
Menu.displayName = 'Menu';
